import { BRAND } from '../config/branding';
import { FLAVORS, getFlavor } from '../data/flavors';
import { FRUITS, getFruit } from '../data/fruits';
import { LEVELS } from '../data/levels';
import type { InputManager } from '../input/InputManager';
import type { GameSimulation } from '../sim/game-simulation';
import type { ControlBindings, SaveData, SettingsData } from '../sim/save';
import type { FlavorId, RunResult } from '../sim/types';

export interface StartSelection {
  levelId: number;
  players: 1 | 2;
  flavors: [FlavorId, FlavorId];
}

export interface FlavorSelection {
  players: 1 | 2;
  flavors: [FlavorId, FlavorId];
}

export class UIManager {
  private readonly root: HTMLElement;
  private readonly touchRoot: HTMLElement;
  private readonly hudElements = new Map<string, HTMLElement>();
  private fruitStageElements: HTMLElement[] = [];

  constructor(root: HTMLElement, touchRoot: HTMLElement) {
    this.root = root;
    this.touchRoot = touchRoot;
  }

  showLoading(): void {
    this.hideTouch();
    this.root.innerHTML = `
      <section class="screen loading-screen" aria-label="Loading">
        <div class="brand-mark small"><i></i><i></i><i></i></div>
        <p class="eyebrow">CHILLING THE SNOWGLOBE</p>
        <h1>${BRAND.title}</h1>
        <div class="loading-track"><span></span></div>
      </section>`;
  }

  showTitle(callbacks: { onPlay: () => void; onSettings: () => void }): void {
    this.hideTouch();
    const fruitParade = FRUITS.map((fruit) =>
      `<i class="fruit-glyph fruit-${fruit.id}" title="${fruit.name}"><i></i></i>`,
    ).join('');
    this.root.innerHTML = `
      <section class="screen title-screen" data-testid="title-screen">
        <div class="rift-orbit" aria-hidden="true"><i></i><i></i><i></i><b></b></div>
        <div class="title-copy">
          <p class="eyebrow">${BRAND.eyebrow}</p>
          <h1>${BRAND.title}</h1>
          <p class="tagline">${BRAND.tagline}</p>
          <p class="brand-rally">${BRAND.rally}</p>
          <div class="title-fruit-showcase" role="img" aria-label="${BRAND.fruitFamilyCount} frozen fruit families to harvest">
            <span class="title-fruit-count"><strong>${BRAND.fruitFamilyCount}</strong><small>FRUIT<br>FAMILIES</small></span>
            <span class="title-fruit-parade" aria-hidden="true">${fruitParade}</span>
          </div>
          <div class="title-actions">
            <button class="primary jumbo" data-action="play" data-testid="play-button"><span>▶</span> Play</button>
            <button class="icon-button" data-action="settings" aria-label="Open settings">⚙</button>
          </div>
          <p class="microcopy"><kbd>WASD</kbd> move · <kbd>SPACE</kbd> build / shatter</p>
        </div>
        <div class="hero-rascals icecat-hero" aria-hidden="true">
          <div class="portrait flavor-cat vanilla-cat"><i class="cat-ear one"></i><i class="cat-ear two"></i><i class="face"></i><i class="muzzle"></i><i class="eye one"></i><i class="eye two"></i><i class="cat-cheek one"></i><i class="cat-cheek two"></i><i class="cat-tail"></i><i class="cat-paw one"></i><i class="cat-paw two"></i><i class="cat-shine"></i><i class="cat-garnish"></i><i class="scarf"></i></div>
          <div class="portrait flavor-cat strawberry-cat"><i class="cat-ear one"></i><i class="cat-ear two"></i><i class="face"></i><i class="muzzle"></i><i class="eye one"></i><i class="eye two"></i><i class="cat-cheek one"></i><i class="cat-cheek two"></i><i class="cat-tail"></i><i class="cat-paw one"></i><i class="cat-paw two"></i><i class="cat-shine"></i><i class="cat-garnish"></i><i class="scarf"></i></div>
        </div>
        <footer>${BRAND.campaignMazeCount} SNOWGLOBE MAZES · ${BRAND.flavorCount} ICE CAT FLAVORS · LOCAL CO-OP</footer>
      </section>`;
    this.button('play', callbacks.onPlay);
    this.button('settings', callbacks.onSettings);
  }

  showFlavorSelect(save: SaveData, callbacks: {
    onContinue: (selection: FlavorSelection) => void;
    onBack: () => void;
    onSettings: () => void;
  }): void {
    this.hideTouch();
    const cards = FLAVORS.map((flavor) => `
      <button class="flavor-card flavor-${flavor.id} ${flavor.id === save.selectedFlavor ? 'selected' : ''}" data-flavor="${flavor.id}" data-testid="flavor-${flavor.id}" aria-pressed="${flavor.id === save.selectedFlavor}">
        <span class="flavor-mascot" aria-hidden="true">
          <i class="mascot-shadow"></i><i class="mascot-tail"></i><i class="mascot-ear left"></i><i class="mascot-ear right"></i>
          <i class="mascot-body"></i><i class="mascot-shine"></i><i class="mascot-eye left"></i><i class="mascot-eye right"></i>
          <i class="mascot-cheek left"></i><i class="mascot-cheek right"></i><i class="mascot-muzzle"></i><i class="mascot-nose"></i>
          <i class="mascot-paw left"></i><i class="mascot-paw right"></i><i class="mascot-scarf"></i><i class="mascot-garnish"></i>
        </span>
        <span class="flavor-icon">${flavor.icon}</span>
        <strong>${flavor.name}</strong>
        <small>${flavor.recipe}</small>
        <b>${flavor.specialName}</b>
      </button>`).join('');
    const p2Options = FLAVORS.map((flavor) => `<option value="${flavor.id}" ${flavor.id === save.selectedSecondFlavor ? 'selected' : ''}>${flavor.name} · ${flavor.specialName}</option>`).join('');
    this.root.innerHTML = `
      <section class="screen flavor-screen" data-testid="flavor-screen">
        <header class="screen-header">
          <button class="icon-button" data-action="back" aria-label="Back">←</button>
          <div><p class="eyebrow">CHOOSE YOUR ICE CAT</p><h2>Pick a Flavor</h2></div>
          <button class="icon-button" data-action="settings" aria-label="Settings">⚙</button>
        </header>
        <p class="flavor-lede">Every flavor can clear every maze. Pick the special style and look that feels like you.</p>
        <div class="flavor-grid" role="group" aria-label="Ice cat flavors">${cards}</div>
        <div class="flavor-footer">
          <div class="segmented" role="group" aria-label="Player count"><button class="active" data-mode="1">1 Ice Cat</button><button data-mode="2">2 Ice Cats</button></div>
          <label class="p2-flavor is-hidden">P2 Flavor <select data-p2-flavor aria-label="Player two flavor">${p2Options}</select></label>
          <button class="primary flavor-continue" data-action="continue" data-testid="flavor-continue">Choose a Snowglobe →</button>
        </div>
      </section>`;
    let playerCount: 1 | 2 = 1;
    let firstFlavor = save.selectedFlavor;
    this.root.querySelectorAll<HTMLButtonElement>('[data-flavor]').forEach((button) => button.addEventListener('click', () => {
      firstFlavor = button.dataset.flavor as FlavorId;
      this.root.querySelectorAll<HTMLButtonElement>('[data-flavor]').forEach((card) => {
        const selected = card === button;
        card.classList.toggle('selected', selected);
        card.setAttribute('aria-pressed', String(selected));
      });
    }));
    this.root.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach((button) => button.addEventListener('click', () => {
      playerCount = button.dataset.mode === '2' ? 2 : 1;
      this.root.querySelectorAll('[data-mode]').forEach((item) => item.classList.toggle('active', item === button));
      this.root.querySelector('.p2-flavor')?.classList.toggle('is-hidden', playerCount === 1);
    }));
    this.button('continue', () => {
      const secondFlavor = (this.root.querySelector<HTMLSelectElement>('[data-p2-flavor]')?.value ?? save.selectedSecondFlavor) as FlavorId;
      callbacks.onContinue({ players: playerCount, flavors: [firstFlavor, secondFlavor] });
    });
    this.button('back', callbacks.onBack);
    this.button('settings', callbacks.onSettings);
  }

  showLevelSelect(save: SaveData, debug: boolean, flavorSelection: FlavorSelection, callbacks: {
    onStart: (selection: StartSelection) => void;
    onBack: () => void;
    onSettings: () => void;
  }): void {
    this.hideTouch();
    const levelCards = LEVELS.map((level) => {
      const unlocked = debug || level.id <= save.unlockedLevel;
      const progress = save.levels[String(level.id)];
      const rank = progress?.bestRank ?? '—';
      const medals = progress?.medals;
      return `
        <button class="level-card chapter-${level.id <= 5 ? 1 : 2} ${level.id > 5 ? 'is-hidden' : ''} ${unlocked ? '' : 'locked'}" data-level="${level.id}" ${unlocked ? '' : 'disabled'} data-testid="level-${level.id}">
          <span class="level-number">${String(level.id).padStart(2, '0')}</span>
          <span class="level-world">${level.kicker}</span>
          <strong>${level.name}</strong>
          <small>${level.objective}</small>
          <span class="level-meta"><b>${rank}</b><i class="${medals?.speed ? 'earned' : ''}">⌁</i><i class="${medals?.noDamage ? 'earned' : ''}">♥</i><i class="${medals?.shard ? 'earned' : ''}">✦</i></span>
          ${unlocked ? '<span class="play-chip">ENTER SNOWGLOBE →</span>' : '<span class="lock-chip">◆ LOCKED</span>'}
        </button>`;
    }).join('');
    const firstFlavor = getFlavor(flavorSelection.flavors[0]);
    this.root.innerHTML = `
      <section class="screen select-screen" data-testid="level-select">
        <header class="screen-header">
          <button class="icon-button" data-action="back" aria-label="Back">←</button>
          <div><p class="eyebrow">CHOOSE YOUR RUN</p><h2>Snowglobe Map</h2></div>
          <button class="icon-button" data-action="settings" aria-label="Settings">⚙</button>
        </header>
        <div class="run-config">
          <div class="chapter-tabs segmented" role="tablist" aria-label="Campaign chapter">
            <button class="active" data-chapter="1" role="tab">Chapter I · Snowfall Circuit</button>
            <button data-chapter="2" role="tab">Chapter II · Aurora Ascent</button>
          </div>
          <span class="selected-flavor"><b>${firstFlavor.icon} ${firstFlavor.name}</b> · ${firstFlavor.specialName}${flavorSelection.players === 2 ? ` + ${getFlavor(flavorSelection.flavors[1]).name}` : ''}</span>
          <span class="debug-badge ${debug ? '' : 'is-hidden'}">DEBUG · ALL SNOWGLOBES OPEN</span>
        </div>
        <div class="level-grid">${levelCards}</div>
        <p class="select-tip"><span>◆</span> Frostwalls block in Normal and become enemy rails in Overdrive.</p>
      </section>`;
    this.root.querySelectorAll<HTMLButtonElement>('[data-chapter]').forEach((button) => button.addEventListener('click', () => {
      const chapter = Number(button.dataset.chapter);
      this.root.querySelectorAll('[data-chapter]').forEach((item) => item.classList.toggle('active', item === button));
      this.root.querySelectorAll<HTMLElement>('.level-card').forEach((card) => card.classList.toggle('is-hidden', !card.classList.contains(`chapter-${chapter}`)));
    }));
    this.root.querySelectorAll<HTMLButtonElement>('[data-level]').forEach((button) => button.addEventListener('click', () => {
      callbacks.onStart({ levelId: Number(button.dataset.level), ...flavorSelection });
    }));
    this.button('back', callbacks.onBack);
    this.button('settings', callbacks.onSettings);
  }

  showSettings(settings: SettingsData, input: InputManager, callbacks: {
    onChange: (settings: SettingsData) => void;
    onBack: () => void;
  }): void {
    this.hideTouch();
    const controls = Object.entries(settings.controls).map(([action, code]) => `
      <label class="binding"><span>${labelAction(action)}</span><button data-bind="${action}">${prettyKey(code)}</button></label>`).join('');
    this.root.innerHTML = `
      <section class="screen settings-screen" data-testid="settings-screen">
        <header class="screen-header"><button class="icon-button" data-action="back" aria-label="Back">←</button><div><p class="eyebrow">MAKE IT YOURS</p><h2>Settings</h2></div><span></span></header>
        <div class="settings-columns">
          <article class="settings-panel"><h3>Audio & Motion</h3>
            <label>Music <input data-setting="musicVolume" type="range" min="0" max="1" step="0.05" value="${settings.musicVolume}"><output>${Math.round(settings.musicVolume * 100)}%</output></label>
            <label>Effects <input data-setting="sfxVolume" type="range" min="0" max="1" step="0.05" value="${settings.sfxVolume}"><output>${Math.round(settings.sfxVolume * 100)}%</output></label>
            <label>Camera punch <input data-setting="shakeStrength" type="range" min="0" max="1" step="0.1" value="${settings.shakeStrength}"><output>${Math.round(settings.shakeStrength * 100)}%</output></label>
            <label class="toggle"><input data-setting="reducedMotion" type="checkbox" ${settings.reducedMotion ? 'checked' : ''}><span></span> Reduced motion</label>
            <label class="toggle"><input data-setting="highContrast" type="checkbox" ${settings.highContrast ? 'checked' : ''}><span></span> High-contrast hazards</label>
            <label>Difficulty <select data-setting="difficulty"><option value="relaxed" ${settings.difficulty === 'relaxed' ? 'selected' : ''}>Relaxed · slower, calmer hunt</option><option value="standard" ${settings.difficulty === 'standard' ? 'selected' : ''}>Standard · intended rhythm</option><option value="expert" ${settings.difficulty === 'expert' ? 'selected' : ''}>Expert · tighter planning</option></select></label>
          </article>
          <article class="settings-panel controls-panel"><h3>P1 Keyboard</h3><div class="bindings">${controls}</div><p>Primary toggle: Space / gamepad A builds, then shatters on the next press. E / B remains an optional quick shatter. P2 uses arrows · numpad 0/1/2.</p></article>
        </div>
      </section>`;
    const mutable: SettingsData = { ...settings, controls: { ...settings.controls } };
    this.root.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-setting]').forEach((element) => element.addEventListener('input', () => {
      const key = element.dataset.setting as keyof SettingsData;
      if (element instanceof HTMLInputElement && element.type === 'checkbox') {
        if (key === 'reducedMotion' || key === 'highContrast') mutable[key] = element.checked;
      } else if (key === 'musicVolume' || key === 'sfxVolume' || key === 'shakeStrength') {
        mutable[key] = Number(element.value);
        const output = element.parentElement?.querySelector('output');
        if (output) output.textContent = `${Math.round(Number(element.value) * 100)}%`;
      } else if (key === 'difficulty') {
        mutable.difficulty = element.value as SettingsData['difficulty'];
      }
      callbacks.onChange(mutable);
    }));
    this.root.querySelectorAll<HTMLButtonElement>('[data-bind]').forEach((button) => button.addEventListener('click', async () => {
      const action = button.dataset.bind as keyof ControlBindings;
      button.textContent = 'PRESS KEY…';
      const code = await input.capture(action);
      mutable.controls[action] = code;
      input.setBindings(mutable.controls);
      button.textContent = prettyKey(code);
      callbacks.onChange(mutable);
    }));
    this.button('back', callbacks.onBack);
  }

  showHUD(simulation: GameSimulation, input: InputManager, callbacks: {
    onPause: () => void;
    onCompleteDebug: () => void;
    onFruitWaveDebug: () => void;
    onShiftDebug: () => void;
    onInvulnerableDebug: () => void;
  }, debug: boolean): void {
    const fruitStages = simulation.fruitWaves.map((wave, index) => {
      const fruit = getFruit(wave.kind);
      return `<div class="fruit-stage ${index === 0 ? 'active' : 'queued'}" data-fruit-stage="${index}" role="listitem" aria-label="Stage ${index + 1}: ${fruit.name}, ${fruit.traitHint} ${index === 0 ? `${wave.cells.length} remaining` : 'queued'}" ${index === 0 ? 'aria-current="step"' : ''}>
        <i class="fruit-glyph fruit-${fruit.id}" aria-hidden="true"><i></i></i>
        <span><b>${fruit.name}</b><small data-fruit-count="${index}">${index === 0 ? `${wave.cells.length} LEFT` : 'QUEUED'}</small></span>
      </div>`;
    }).join('<em role="presentation" aria-hidden="true">›</em>');
    this.root.innerHTML = `
      <section class="hud" data-testid="game-hud">
        <div class="hud-top" data-gameplay-text-surface>
          <div class="hud-brand"><span class="state-symbol">◇</span><strong>${simulation.definition.name}</strong><small>${simulation.definition.kicker}</small></div>
          <div class="objective-pill"><span data-hud="cores">1/${simulation.fruitWaves.length}</span><small>FRUIT STAGES</small></div>
          <div class="objective-pill"><span data-hud="combo">×1</span><small>COMBO</small></div>
          <div class="timer-pill"><span data-hud="timer">00:00.0</span><small>RUN TIME</small></div>
          <button class="pause-button" data-action="pause" aria-label="Pause">Ⅱ</button>
        </div>
        <aside class="player-hud p1" data-gameplay-text-surface><div class="player-token flavor-${simulation.players[0]?.flavor ?? 'vanilla'}">P1</div><div><strong data-hud="p1-health">♥</strong><span data-hud="p1-walls">◆ BUILD</span><small data-hud="p1-special">SPECIAL READY</small></div></aside>
        <aside class="player-hud p2 ${simulation.players.length === 2 ? '' : 'is-hidden'}" data-gameplay-text-surface><div class="player-token flavor-${simulation.players[1]?.flavor ?? 'strawberry'}">P2</div><div><strong data-hud="p2-health">♥</strong><span data-hud="p2-walls">◆ BUILD</span><small data-hud="p2-special">SPECIAL READY</small></div></aside>
        <section class="fruit-progress" data-gameplay-text-surface data-stage-count="${simulation.fruitWaves.length}" data-testid="fruit-tracker" aria-label="Fruit stages">
          <header><span aria-label="Active fruit family"><i class="active-indicator" aria-hidden="true">●</i><b data-hud="active-fruit">${getFruit(simulation.activeFruitWave.kind).plural.toUpperCase()}</b></span><em class="fruit-trait" data-hud="fruit-trait" aria-hidden="true">${getFruit(simulation.activeFruitWave.kind).traitIcon} ${getFruit(simulation.activeFruitWave.kind).traitLabel}</em><small data-hud="fruit-types">${simulation.getFruitTypesRemainingCount()} TYPES LEFT</small></header>
          <div class="fruit-stage-list" role="list">${fruitStages}</div>
          <p class="sr-only" data-hud="fruit-status" aria-live="polite"></p>
        </section>
        <aside class="arena-status-rail" data-gameplay-text-surface data-testid="arena-status-rail" aria-label="Arena status outside the snow maze">
          <div class="shift-panel" data-gameplay-text-surface data-state="normal"><span class="state-symbol" aria-hidden="true">◇</span><div><strong data-hud="shift-state">NORMAL</strong><small data-hud="shift-time">STABLE</small></div></div>
          <div class="mission-card" data-gameplay-text-surface><span aria-hidden="true">◎</span><div><small>OBJECTIVE</small><strong data-hud="mission">${simulation.definition.objective}</strong></div></div>
          <ol class="notification-stack" data-gameplay-text-surface data-testid="notification-rail" role="log" aria-label="Game notifications" aria-live="polite" aria-relevant="additions"></ol>
        </aside>
        <div class="debug-strip ${debug ? '' : 'is-hidden'}" data-debug-text-surface><b>DEBUG</b><button data-debug="fruit-wave">Next fruit</button><button data-debug="complete">Complete all</button><button data-debug="shift">Shift</button><button data-debug="invulnerable">Invulnerable</button></div>
      </section>`;
    this.hudElements.clear();
    this.root.querySelectorAll<HTMLElement>('[data-hud]').forEach((element) => {
      const key = element.dataset.hud;
      if (key) this.hudElements.set(key, element);
    });
    this.fruitStageElements = [...this.root.querySelectorAll<HTMLElement>('[data-fruit-stage]')];
    this.showTouch(input);
    this.button('pause', callbacks.onPause);
    this.root.querySelector('[data-debug="complete"]')?.addEventListener('click', callbacks.onCompleteDebug);
    this.root.querySelector('[data-debug="fruit-wave"]')?.addEventListener('click', callbacks.onFruitWaveDebug);
    this.root.querySelector('[data-debug="shift"]')?.addEventListener('click', callbacks.onShiftDebug);
    this.root.querySelector('[data-debug="invulnerable"]')?.addEventListener('click', callbacks.onInvulnerableDebug);
    this.updateHUD(simulation);
  }

  updateHUD(simulation: GameSimulation): void {
    const harvestComplete = simulation.isHarvestComplete();
    const completed = harvestComplete ? simulation.fruitWaves.length : simulation.activeFruitWaveIndex;
    const activeRemaining = simulation.getActiveFruitRemainingCount();
    const fruitTypesRemaining = simulation.getFruitTypesRemainingCount();
    const activeFruit = getFruit(simulation.activeFruitWave.kind);
    const trait = summarizeFruitTrait(simulation);
    this.setHUD('cores', harvestComplete ? 'DONE' : `${simulation.activeFruitWaveIndex + 1}/${simulation.fruitWaves.length}`);
    this.setHUD('combo', `×${Math.max(1, simulation.scoring.state.combo)}`);
    this.setHUD('timer', formatTime(Math.floor(simulation.tickCount / 6) * 100));
    this.setHUD('active-fruit', harvestComplete ? 'HARVEST COMPLETE' : activeFruit.plural.toUpperCase());
    this.setHUD('fruit-trait', harvestComplete ? '✓ ALL CLEAR' : trait.label);
    const fruitTrait = this.hudElements.get('fruit-trait');
    if (fruitTrait) fruitTrait.title = harvestComplete ? 'Every fruit family has been collected.' : trait.description;
    this.setHUD('fruit-types', `${fruitTypesRemaining} ${fruitTypesRemaining === 1 ? 'TYPE' : 'TYPES'} LEFT`);
    this.setHUD('fruit-status', harvestComplete
      ? 'Fruit harvest complete.'
      : `${activeFruit.plural}, ${activeRemaining} remaining. ${trait.description} ${fruitTypesRemaining} fruit ${fruitTypesRemaining === 1 ? 'type' : 'types'} remaining.`);
    this.fruitStageElements.forEach((stage) => {
      const index = Number(stage.dataset.fruitStage);
      const fruit = getFruit(simulation.fruitWaves[index]?.kind ?? simulation.activeFruitWave.kind);
      const isActive = !harvestComplete && index === simulation.activeFruitWaveIndex;
      stage.classList.toggle('completed', index < completed);
      stage.classList.toggle('active', isActive);
      stage.classList.toggle('queued', !harvestComplete && index > simulation.activeFruitWaveIndex);
      if (isActive) stage.setAttribute('aria-current', 'step');
      else stage.removeAttribute('aria-current');
      const count = stage.querySelector<HTMLElement>('[data-fruit-count]');
      const state = index < completed || harvestComplete ? 'cleared'
        : isActive ? `${activeRemaining} remaining`
          : 'queued';
      const countLabel = state === 'cleared' ? '✓ CLEARED' : isActive ? `${activeRemaining} LEFT` : 'QUEUED';
      if (count && count.textContent !== countLabel) count.textContent = countLabel;
      stage.setAttribute('aria-label', `Stage ${index + 1}: ${fruit.name}. ${fruit.traitHint} ${state}`);
    });
    for (const player of simulation.players) {
      this.setHUD(`p${player.id}-health`, player.downed ? 'THAWED' : `${'♥'.repeat(Math.max(0, player.health))}${'♡'.repeat(Math.max(0, player.maxHealth - player.health))}`);
      const lines = simulation.flipwalls.ownerLineCount(player.id);
      this.setHUD(`p${player.id}-walls`, lines === 0 ? '◆ BUILD' : '✹ REMOVE');
      const cooldown = player.specialCooldownTicks;
      this.setHUD(`p${player.id}-special`, cooldown === 0 ? `${getFlavor(player.flavor).specialName.toUpperCase()} READY` : `${(cooldown / 60).toFixed(1)}s`);
    }
    const shiftTicks = simulation.getShiftCountdownTicks();
    this.setHUD('shift-state', simulation.worldState.toUpperCase());
    this.setHUD('shift-time', shiftTicks === null ? 'STABLE' : `SHIFT ${(shiftTicks / 60).toFixed(1)}s`);
    const shiftPanel = this.root.querySelector<HTMLElement>('.shift-panel');
    if (shiftPanel) shiftPanel.dataset.state = simulation.worldState;
    if (simulation.definition.boss) {
      const mission = simulation.bossPhase === 1 ? `SHIELD FRUITS · ${simulation.coresRemaining.size} LEFT`
        : simulation.bossPhase === 2 ? `REDIRECT ENERGY · ${simulation.bossRedirects}/3 WALLS`
          : simulation.bossPhase === 3 ? `ENERGIZED SHATTERS · ${simulation.bossOverloads}/3`
            : 'FINAL BREACH · REACH THE SNOWGATE';
      this.setHUD('mission', mission);
    } else if (simulation.portalOpen) this.setHUD('mission', 'EXTRACTION OVERDRIVE · REACH THE SNOWGATE');
  }

  celebrateFruitPickup(waveIndex: number): void {
    this.animateFruitStage(waveIndex, 'pickup-bump', 330);
  }

  celebrateFruitWave(waveIndex: number): void {
    this.animateFruitStage(waveIndex, 'wave-reveal', 560);
  }

  showPause(callbacks: { onResume: () => void; onRestart: () => void; onSettings: () => void; onQuit: () => void }): void {
    this.removeOverlay();
    this.root.insertAdjacentHTML('beforeend', `
      <section class="modal-layer" data-testid="pause-menu"><div class="modal pause-modal"><p class="eyebrow">SNOWGLOBE SUSPENDED</p><h2>Paused</h2><button class="primary" data-modal="resume">Resume</button><button data-modal="restart">Fast restart</button><button data-modal="settings">Settings</button><button data-modal="quit">Snowglobe map</button></div></section>`);
    this.modalButton('resume', callbacks.onResume);
    this.modalButton('restart', callbacks.onRestart);
    this.modalButton('settings', callbacks.onSettings);
    this.modalButton('quit', callbacks.onQuit);
  }

  hidePause(): void {
    this.removeOverlay();
  }

  showResults(result: RunResult, isFinal: boolean, callbacks: { onReplay: () => void; onNext: () => void; onMap: () => void }): void {
    this.hideTouch();
    this.root.innerHTML = `
      <section class="screen results-screen" data-testid="results-screen">
        <div class="result-rays" aria-hidden="true"></div>
        <p class="eyebrow">${isFinal ? 'THE WINTER CROWN IS YOURS' : 'SNOWGLOBE CLEARED'}</p>
        <h2>${isFinal ? 'Flavor Champion!' : LEVELS[result.levelId - 1]?.name ?? 'Clear'}</h2>
        <div class="rank-medallion rank-${result.rank}"><span>${result.rank}</span><small>RANK</small></div>
        <div class="result-stats"><div><strong>${formatTime(result.timeMs)}</strong><small>TIME</small></div><div><strong>${result.score.toLocaleString()}</strong><small>SCORE</small></div><div><strong>×${result.maxCombo}</strong><small>MAX COMBO</small></div></div>
        <div class="medals"><div class="${result.speedMedal ? 'earned' : ''}"><b>⌁</b><span>Speed</span></div><div class="${result.noDamageMedal ? 'earned' : ''}"><b>♥</b><span>No damage</span></div><div class="${result.shardMedal ? 'earned' : ''}"><b>✦</b><span>Sugar Shard</span></div></div>
        <div class="result-actions"><button data-action="replay">↻ Replay</button><button data-action="map">Snowglobe map</button><button class="primary" data-action="next">${isFinal ? 'Celebrate again' : 'Next snowglobe'} →</button></div>
      </section>`;
    this.button('replay', callbacks.onReplay);
    this.button('map', callbacks.onMap);
    this.button('next', callbacks.onNext);
  }

  showFailure(label: string, callbacks: { onRestart: () => void; onMap: () => void }): void {
    this.hideTouch();
    const thawed = label.includes('THAWED');
    this.root.innerHTML = `
      <section class="screen failure-screen" data-testid="failure-screen"><div class="failure-glyph">${thawed ? '≈' : '❄'}</div><p class="eyebrow">${label}</p><h2>${thawed ? 'Frost shell released.' : 'Snowed under.'}</h2><p>${thawed ? 'Freeze back in and outsmart the patrol.' : 'The trail remembers. Try a sharper turn.'}</p><div class="result-actions"><button data-action="map">Snowglobe map</button><button class="primary" data-action="restart">↻ Restart now</button></div><small>Press R for instant restart</small></section>`;
    this.button('restart', callbacks.onRestart);
    this.button('map', callbacks.onMap);
  }

  toast(
    message: string,
    tone: 'good' | 'warn' | 'danger' = 'good',
    icon?: string,
    durationMs = 1_450,
  ): void {
    const stack = this.root.querySelector<HTMLOListElement>('.notification-stack');
    if (!stack) return;

    const item = document.createElement('li');
    item.className = 'notification-item';
    item.dataset.tone = tone;
    item.dataset.testid = 'notification-item';

    const symbol = document.createElement('span');
    symbol.className = 'notification-icon';
    symbol.setAttribute('aria-hidden', 'true');
    symbol.textContent = icon ?? (tone === 'danger' ? '×' : tone === 'warn' ? '!' : '✦');

    const label = document.createElement('span');
    label.className = 'notification-copy';
    label.textContent = message;
    item.append(symbol, label);

    while (stack.children.length >= 3) stack.firstElementChild?.remove();
    stack.append(item);
    window.requestAnimationFrame(() => item.classList.add('visible'));
    window.setTimeout(() => {
      item.classList.remove('visible');
      window.setTimeout(() => item.remove(), 190);
    }, durationMs);
  }

  clear(): void {
    this.root.innerHTML = '';
    this.hudElements.clear();
    this.fruitStageElements = [];
    this.hideTouch();
  }

  private showTouch(input: InputManager): void {
    this.touchRoot.innerHTML = `
      <div class="touch-dpad"><button data-touch="up" aria-label="Move up">▲</button><button data-touch="left" aria-label="Move left">◀</button><button data-touch="down" aria-label="Move down">▼</button><button data-touch="right" aria-label="Move right">▶</button></div>
      <div class="touch-actions"><button class="touch-special" data-touch="special" aria-label="Special ability">✦</button><button class="touch-wall" data-touch="wall" aria-label="Build or shatter Frostwall">❄</button></div>`;
    this.touchRoot.classList.add('active');
    input.bindTouchControls(this.touchRoot);
  }

  private hideTouch(): void {
    this.touchRoot.classList.remove('active');
    this.touchRoot.innerHTML = '';
  }

  private button(action: string, handler: () => void): void {
    this.root.querySelector(`[data-action="${action}"]`)?.addEventListener('click', handler);
  }

  private modalButton(action: string, handler: () => void): void {
    this.root.querySelector(`[data-modal="${action}"]`)?.addEventListener('click', handler);
  }

  private removeOverlay(): void {
    this.root.querySelector('.modal-layer')?.remove();
  }

  private setHUD(key: string, value: string): void {
    const cached = this.hudElements.get(key);
    const element = cached?.isConnected ? cached : this.root.querySelector<HTMLElement>(`[data-hud="${key}"]`);
    if (!element) return;
    this.hudElements.set(key, element);
    if (element.textContent !== value) element.textContent = value;
  }

  private animateFruitStage(index: number, className: string, duration: number): void {
    const stage = this.fruitStageElements[index];
    if (!stage?.isConnected) return;
    stage.classList.remove(className);
    void stage.offsetWidth;
    stage.classList.add(className);
    window.setTimeout(() => stage.classList.remove(className), duration);
  }
}

function summarizeFruitTrait(simulation: GameSimulation): { label: string; description: string } {
  const fruit = getFruit(simulation.activeFruitWave.kind);
  const actors = simulation.getActiveFruitActors();
  const count = (phase: (typeof actors)[number]['phase']): number => actors.filter((actor) => actor.phase === phase).length;
  const airborne = count('airborne');
  if (airborne > 0) return {
    label: `⌁ ${airborne} AIRBORNE`,
    description: `${airborne} ${airborne === 1 ? 'pineapple is' : 'pineapples are'} airborne and cannot be caught until landing.`,
  };
  const phasing = count('teleporting');
  if (phasing > 0) return {
    label: `✦ ${phasing} PHASING`,
    description: `${phasing} cherry ${phasing === 1 ? 'pair is' : 'pairs are'} phasing and cannot be caught until reappearing.`,
  };
  if (count('flight-warning') > 0) return { label: '⌁ TAKEOFF!', description: 'Leaf rotors are spinning up. Catch the pineapples before they lift off.' };
  if (count('teleport-warning') > 0) return { label: '✦ PHASE SOON', description: 'The cherry phase rings reveal an incoming teleport.' };
  if (count('run-warning') > 0) return { label: '➜ DASH SOON', description: 'The Snow Pears are preparing a quick, telegraphed dash.' };
  if (count('drift-warning') > 0) return { label: '↝ WADDLE SOON', description: 'The Strawberries are preparing a slow one-tile waddle.' };
  if (count('running') > 0) return { label: '➜ RUNNING', description: 'Snow Pears are dashing between safe snow tiles.' };
  if (count('drifting') > 0) return { label: '↝ WANDERING', description: 'Strawberries are waddling slowly and remain catchable.' };
  if (count('landing') > 0) return { label: '⌁ LANDING', description: 'Pineapples are touching down and can be caught again.' };
  return { label: `${fruit.traitIcon} ${fruit.traitLabel}`, description: fruit.traitHint };
}

function labelAction(action: string): string {
  if (action === 'wall') return 'Frostwall toggle';
  if (action === 'shatter') return 'Quick shatter';
  return action.replace(/([A-Z])/g, ' $1').replace(/^./, (value) => value.toUpperCase());
}

function prettyKey(code: string): string {
  return code.replace('Key', '').replace('Digit', '').replace('Arrow', 'Arrow ').replace('Left', ' L').replace('Right', ' R');
}

export function formatTime(ms: number): string {
  const totalSeconds = ms / 1000;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  const tenths = Math.floor((ms % 1000) / 100);
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${tenths}`;
}
